import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchOperationsManagementSettings,
  saveOperationsManagementSettings,
} from "../../../../services/operationsManagement";

export const DEFAULT_PRODUCTION_SETTINGS = {
  onHoldWebNotificationEnabled: true,
  onHoldEmailNotificationEnabled: true,
  onHoldNotifyOrderReps: true,
  onHoldNotifyPurchasingReps: true,
  completionWebNotificationEnabled: true,
  completionEmailNotificationEnabled: true,
  completionNotifyOrderReps: true,
  completionNotifyPurchasingReps: true,
  completionNotifyShipping: true,
  completedRetentionHours: 24,
  archiveEnabled: true,
  archiveScope: "project",
};

export const normalizeProductionRetentionInput = (
  value,
  fallback = DEFAULT_PRODUCTION_SETTINGS.completedRetentionHours
) => {
  const raw = String(value ?? "").trim();
  const fallbackNumber = Number(fallback);
  const safeFallback = Number.isFinite(fallbackNumber)
    ? Math.min(8760, Math.max(0, fallbackNumber))
    : DEFAULT_PRODUCTION_SETTINGS.completedRetentionHours;
  if (!raw) return safeFallback;

  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric >= 0 && numeric <= 8760
    ? numeric
    : safeFallback;
};

const ProductionSettingsPanel = ({ instanceUniqueId, canManage = false }) => {
  const [settings, setSettings] = useState(DEFAULT_PRODUCTION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManageFoundation, setCanManageFoundation] = useState(false);
  const [error, setError] = useState("");
  const [retentionInput, setRetentionInput] = useState(
    String(DEFAULT_PRODUCTION_SETTINGS.completedRetentionHours)
  );

  const productionSettings = useMemo(
    () => ({ ...DEFAULT_PRODUCTION_SETTINGS, ...settings }),
    [settings]
  );

  const loadSettings = useCallback(async () => {
    if (!instanceUniqueId) return;

    setLoading(true);
    setError("");
    try {
      const response = await fetchOperationsManagementSettings(instanceUniqueId);
      setCanManageFoundation(response?.permissions?.canManageFoundationalSettings === true);
      const nextSettings = {
        ...DEFAULT_PRODUCTION_SETTINGS,
        ...(response?.settings?.production || {}),
      };
      const normalizedRetention = normalizeProductionRetentionInput(
        nextSettings.completedRetentionHours
      );
      setSettings({ ...nextSettings, completedRetentionHours: normalizedRetention });
      setRetentionInput(String(normalizedRetention));
    } catch (loadError) {
      setCanManageFoundation(false);
      setError(loadError?.response?.data?.error || "Unable to load Production settings.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveProductionSettings = async (updates) => {
    const nextSettings = {
      ...productionSettings,
      ...updates,
      completedRetentionHours: normalizeProductionRetentionInput(
        updates.completedRetentionHours ?? productionSettings.completedRetentionHours,
        productionSettings.completedRetentionHours
      ),
    };
    setSaving(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        production: nextSettings,
      });
      const savedSettings = {
        ...DEFAULT_PRODUCTION_SETTINGS,
        ...(response?.settings?.production || nextSettings),
      };
      const normalizedRetention = normalizeProductionRetentionInput(
        savedSettings.completedRetentionHours
      );
      setSettings({ ...savedSettings, completedRetentionHours: normalizedRetention });
      setRetentionInput(String(normalizedRetention));
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Unable to save Production settings.");
    } finally {
      setSaving(false);
    }
  };

  const commitRetentionInput = async () => {
    const normalizedRetention = normalizeProductionRetentionInput(
      retentionInput,
      productionSettings.completedRetentionHours
    );
    setRetentionInput(String(normalizedRetention));
    if (normalizedRetention === Number(productionSettings.completedRetentionHours)) return;
    await saveProductionSettings({ completedRetentionHours: normalizedRetention });
  };

  const renderToggle = (key, label) => (
    <FormControlLabel
      key={key}
      control={
        <Checkbox
          checked={productionSettings[key] !== false}
          disabled={!canManage || (key === "archiveEnabled" && !canManageFoundation) || saving || loading}
          onChange={(event) => saveProductionSettings({ [key]: event.target.checked })}
        />
      }
      label={label}
    />
  );

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Stack spacing={0.5}>
        <Typography variant="h6">Production Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure Production lifecycle notifications and Completed order retention for this instance.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {canManage && !canManageFoundation && !loading ? (
        <Alert severity="info">Your permission level cannot change protected Production archiving settings. Please contact a Super Administrator for this instance to request a change. Notifications remain editable.</Alert>
      ) : null}
      {!canManage ? (
        <Alert severity="info">You can view Production settings, but only managers and administrators can change them.</Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
            <Typography variant="body2" color="text.secondary">Choose which Production lifecycle events generate notifications.</Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>On Hold</Typography>
            {[
              ["onHoldWebNotificationEnabled", "On Hold web notifications"],
              ["onHoldEmailNotificationEnabled", "On Hold email notifications"],
              ["onHoldNotifyOrderReps", "Notify all Order Reps"],
              ["onHoldNotifyPurchasingReps", "Notify all Purchasing Reps"],
            ].map(([key, label]) => renderToggle(key, label))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Production Complete</Typography>
            {[
              ["completionWebNotificationEnabled", "Completion web notifications"],
              ["completionEmailNotificationEnabled", "Completion email notifications"],
              ["completionNotifyOrderReps", "Notify all Order Reps"],
              ["completionNotifyPurchasingReps", "Notify all Purchasing Reps"],
              ["completionNotifyShipping", "Notify active Shipping Reps"],
            ].map(([key, label]) => renderToggle(key, label))}
          </Stack>
        </CardContent>
      </Card>

      {canManageFoundation ? (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Completed Orders</Typography>
            {renderToggle("archiveEnabled", "Enable Archive")}
            <FormControl disabled={!canManage || !canManageFoundation || saving || loading}>
              <FormLabel>Archive completed work by</FormLabel>
              <RadioGroup
                value={productionSettings.archiveScope}
                onChange={(event) => saveProductionSettings({ archiveScope: event.target.value })}
              >
                <FormControlLabel value="project" control={<Radio />} label="Entire Project" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Keep completed work visible until all Production departments for the project are finished.
                </Typography>
                <FormControlLabel value="department" control={<Radio />} label="Each Department" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Archive a department&apos;s completed work independently once all Production work for that department is finished.
                </Typography>
              </RadioGroup>
            </FormControl>
            <TextField
              label="Completed retention (hours)"
              type="number"
              size="small"
              value={retentionInput}
              disabled={!canManage || !canManageFoundation || saving || loading}
              inputProps={{ min: 0, max: 8760 }}
              onChange={(event) => setRetentionInput(event.target.value)}
              onBlur={commitRetentionInput}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              helperText={productionSettings.archiveScope === "department"
                ? "Hours to keep completed department work visible after that department is complete. Use 0 for immediate archive."
                : "Hours to keep completed work visible after the entire project is complete. Use 0 for immediate archive."}
            />
          </Stack>
        </CardContent>
      </Card>
      ) : null}

      {loading ? <Alert severity="info">Loading Production settings...</Alert> : null}
    </Box>
  );
};

export default ProductionSettingsPanel;
